import {FeatureDefinition} from 'auth/feature-enablement/FeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {
  STEP_TYPE_TO_CONFIG_FLAGS,
  STEP_TYPE_TO_REQUIRED_CAPABILITY_MANAGEMENT_FEATURES,
  STEP_TYPE_TO_USER_CAN_CREATE_DIRECTLY,
} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';

type BenchlingConfigKey = keyof typeof BenchlingConfig;

function isConfigFlagEnabled(flag: string): boolean {
  return Boolean(BenchlingConfig[flag as BenchlingConfigKey]);
}

function isCapabilityManagementFeatureEnabled(feature: string): boolean {
  return isFeatureEnabled({feature} as FeatureDefinition);
}

/**
 * Check whether a step type is enabled for the current user to create and run.
 *
 * This gating derives from these fields of the step type's YAML manifest:
 * - `configFlags`
 * - `capabilityManagementFeatures`
 * - `userCanCreateDirectly` (if `forCreate` is `true`)
 *
 * `configFlags` and `capabilityManagementFeatures` gate the ability to create or run a step.
 * `userCanCreateDirectly` gates the ability to directly create a new step, but allows updating
 * and running existing steps, and creating steps indirectly (e.g. by applying a static template).
 *
 * @param forCreate - Set true to check if the user can create a step; set false to check if the
 *   user can update or run a step.
 */
export default function isStepTypeEnabledForUser(
  stepType: PipelineStepType,
  forCreate: boolean = true
): boolean {
  const configFlags = STEP_TYPE_TO_CONFIG_FLAGS[stepType];
  if (!configFlags.every((flag) => isConfigFlagEnabled(flag))) {
    return false;
  }

  const capabilityManagementFeatures = STEP_TYPE_TO_REQUIRED_CAPABILITY_MANAGEMENT_FEATURES[stepType];
  if (!capabilityManagementFeatures.every((feature) => isCapabilityManagementFeatureEnabled(feature))) {
    return false;
  }

  if (forCreate && !STEP_TYPE_TO_USER_CAN_CREATE_DIRECTLY[stepType]) {
    return false;
  }

  return true;
}
